import React from "react";
// eslint-disable-next-line import/extensions
import type { PDFViewer } from "pdfjs-dist/legacy/web/pdf_viewer.mjs";
import { viewportToScaled } from "components/pdf-highlighter/lib";
import { LTWHP, Scaled, LTWH, ScaledPosition, IHighlight, TViewportHighlight, Position } from "components/pdf-highlighter/types";


interface HighlightLayerProps<THT> {
    highlightsByPage: { [pageNumber: string]: Array<THT> };
    pageNumber: string;
    scrolledToHighlightId: string;
    highlightTransform: (
        highlight: TViewportHighlight<THT>,
        index: number,
        setTip: (
            highlight: TViewportHighlight<THT>,
            callback: (highlight: TViewportHighlight<THT>) => JSX.Element,
        ) => void,
        hideTip: () => void,
        viewportToScaled: (rect: LTWHP) => Scaled,
        screenshot: (position: LTWH) => string,
        isScrolledTo: boolean,
    ) => JSX.Element;
    tip: {
        highlight: TViewportHighlight<THT>;
        callback: (highlight: TViewportHighlight<THT>) => JSX.Element;
    } | null;
    scaledPositionToViewport: (scaledPosition: ScaledPosition) => Position;
    hideTipAndSelection: () => void;
    viewer: PDFViewer;
    screenshot: (position: LTWH, pageNumber: number) => string;
    showTip: (highlight: TViewportHighlight<THT>, content: JSX.Element) => void;
    setTip: (state: {
        highlight: TViewportHighlight<THT>;
        callback: (highlight: TViewportHighlight<THT>) => JSX.Element;
    }) => void;
}

export const HighlightLayer = <THT extends IHighlight>({
    highlightsByPage,
    scaledPositionToViewport,
    pageNumber,
    scrolledToHighlightId,
    highlightTransform,
    tip,
    hideTipAndSelection,
    viewer,
    screenshot,
    showTip,
    setTip,
}: HighlightLayerProps<THT>) => {
    const currentHighlights = highlightsByPage[String(pageNumber)] || [];
    return (
        <div>
            {currentHighlights.map((highlight, index) => {
                const viewportHighlight: TViewportHighlight<THT> = {
                    ...highlight,
                    position: scaledPositionToViewport(highlight.position),
                };

                if (tip && tip.highlight.id === String(highlight.id)) {
                    showTip(tip.highlight, tip.callback(viewportHighlight));
                }

                const isScrolledTo = Boolean(scrolledToHighlightId === highlight.id);

                return highlightTransform(
                    viewportHighlight,
                    index,
                    (highlightParam, callback) => {
                        setTip({ highlight: highlightParam, callback });
                        showTip(highlightParam, callback(highlightParam));
                    },
                    hideTipAndSelection,
                    (rect) => {
                        const { viewport } = viewer.getPageView(
                            (rect.pageNumber || Number.parseInt(pageNumber, 10)) - 1,
                        );

                        return viewportToScaled(rect, viewport);
                    },
                    (boundingRect) =>
                        screenshot(boundingRect, Number.parseInt(pageNumber, 10)),
                    isScrolledTo,
                );
            })}
        </div>
    );
};